import { useRef, useState, useEffect } from "react";
import { UploadIcon } from "../Components/Icons";
import { compressImage } from "../lib/images/compressImage.js";
import "./ImageUploadButton.css";

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * onUploadImage(kind, file) should upload + persist the reference and
 * resolve to { kind: "reference", id } (or null on failure). This button
 * doesn't touch doc state itself — onUploaded receives the result so the
 * caller decides where it goes (a slot, a layer, or the shared background).
 *
 * Logos are deliberately excluded from the compress step - the client's
 * most important asset, left entirely in their own control.
 */
export default function ImageUploadButton({ kind, onUploadImage, isUploadingImage, onUploaded, disabled }) {
  const fileInputRef = useRef(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [compressedFile, setCompressedFile] = useState(null);
  const [isCompressing, setIsCompressing] = useState(false);
  const [compressError, setCompressError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const activeFile = compressedFile ?? pendingFile;
  const canCompress = kind !== "logo" && !compressedFile;

  useEffect(() => {
    if (!pendingFile) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(pendingFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pendingFile]);

  function handleFileSelected(event) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setPendingFile(file);
    setCompressedFile(null);
    setCompressError(null);
  }

  async function handleCompress() {
    setIsCompressing(true);
    setCompressError(null);
    try {
      const result = await compressImage(pendingFile);
      if (result.size < pendingFile.size) {
        setCompressedFile(result);
      } else {
        // Confirmed via testing: simple/flat-color images can grow under
        // canvas re-encoding - only ever offer the compressed version
        // when it's genuinely smaller, never let it silently replace a
        // better original.
        setCompressError(
          "Compression didn't reduce the size for this image — this can happen with detailed, " +
            "semi-transparent images, since PNG re-encoding has no way to trade quality for size the way JPEG does."
        );
      }
    } catch (err) {
      setCompressError(err.message || "Couldn't compress this image.");
    } finally {
      setIsCompressing(false);
    }
  }

  function handleCancel() {
    setPendingFile(null);
    setCompressedFile(null);
    setCompressError(null);
  }

  async function handleConfirmUpload() {
    const source = await onUploadImage(kind, activeFile);
    if (source) {
      onUploaded(source);
      setPendingFile(null);
      setCompressedFile(null);
      setCompressError(null);
    }
  }

  if (pendingFile) {
    return (
      <div className="image-upload-btn__preview">
        {previewUrl ? (
          <img src={previewUrl} alt="" className="image-upload-btn__preview-thumb" />
        ) : null}
        <div className="image-upload-btn__preview-info">
          <span className="image-upload-btn__preview-name">{pendingFile.name}</span>
          <span className="image-upload-btn__preview-size">
            {compressedFile ? (
              <>
                <s>{formatBytes(pendingFile.size)}</s> → {formatBytes(compressedFile.size)} (
                {Math.round((1 - compressedFile.size / pendingFile.size) * 100)}% smaller, as{" "}
                {compressedFile.type === "image/webp" ? "WebP" : "JPEG"})
              </>
            ) : (
              formatBytes(pendingFile.size)
            )}
          </span>
          {compressedFile?.type === "image/webp" ? (
            <span className="image-upload-btn__preview-note">
              WebP keeps transparency with real compression, unlike PNG — but isn't explicitly
              confirmed supported by every ad platform's HTML5 creative pipeline.
            </span>
          ) : null}
          {compressError ? <span className="image-upload-btn__preview-error">{compressError}</span> : null}
        </div>
        <div className="image-upload-btn__preview-actions">
          {canCompress ? (
            <button
              type="button"
              onClick={handleCompress}
              disabled={isCompressing || isUploadingImage}
              className="image-upload-btn__preview-btn"
            >
              {isCompressing ? "Compressing…" : "Compress"}
            </button>
          ) : null}
          <button
            type="button"
            onClick={handleConfirmUpload}
            disabled={isUploadingImage}
            className="image-upload-btn__preview-btn image-upload-btn__preview-btn--primary"
          >
            {isUploadingImage ? "Uploading…" : "Upload"}
          </button>
          <button
            type="button"
            onClick={handleCancel}
            disabled={isUploadingImage}
            className="image-upload-btn__preview-btn"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <label
      className={`image-upload-btn${isUploadingImage || disabled ? " image-upload-btn--disabled" : ""}`}
    >
      {isUploadingImage ? (
        <>
          <span className="image-upload-btn__spinner" />
          Uploading…
        </>
      ) : (
        <>
          <UploadIcon className="image-upload-btn__icon" />
          Upload background / image
        </>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        disabled={isUploadingImage || disabled}
        onChange={handleFileSelected}
        className="image-upload-btn__input"
      />
    </label>
  );
}